(function () {
  const TERMINAL_CASE_STATUSES = new Set([
    'AI_COMPLETED',
    'AI_PARTIAL_FAILED',
    'AI_FAILED'
  ]);

  const sleep = (delayMs) => new Promise((resolve) => window.setTimeout(resolve, delayMs));

  const serializeSurfaces = (state, studioSnapshot) => {
    const source = state.surfaces || studioSnapshot.surfaces || {};
    return Object.keys(source).reduce((surfaces, side) => {
      const surface = source[side] || {};
      surfaces[side] = {
        sourceType: surface.sourceType || (surface.templateId ? 'template' : 'upload'),
        templateId: surface.templateId || undefined,
        customText: surface.customText || surface.text || undefined,
        textFont: surface.textFont || undefined,
        textColor: surface.textColor || undefined,
        textPosition: surface.textPosition || undefined,
        hasUploadedImage: Boolean(surface.uploadedFile)
      };
      return surfaces;
    }, {});
  };

  const createDesignPayload = (state, studioSnapshot) => ({
    anonymousId: studioSnapshot.anonymousId,
    sceneId: state.selectedSceneId || undefined,
    eventId: state.selectedEventId || undefined,
    templateId: state.templateId || undefined,
    materialPresetId: studioSnapshot.materialPresetId,
    generatePreview: true,
    customText: state.customText && state.customText.trim() ? state.customText.trim() : undefined,
    caseConfig: {
      productType: studioSnapshot.productType || 'FLANNEL_SINGLE_PRINT',
      size: studioSnapshot.size || '50 x 60',
      artworkMode: studioSnapshot.productMode || 'customizable',
      fixedArtworkUrl: studioSnapshot.fixedArtworkUrl || undefined,
      hasUploadedImage: Boolean(state.uploadedFile),
      previewMode: 'five-images',
      surfaces: serializeSurfaces(state, studioSnapshot)
    },
    labelConfig: {
      presetId: studioSnapshot.brandMarkPresetId,
      style: state.labelStyle,
      hasLogo: Boolean(state.logoFile)
    },
    packageConfig: {
      presetId: studioSnapshot.packagingPresetId,
      style: state.packagingStyle,
      hasLogo: Boolean(state.logoFile)
    },
    cardConfig: {
      presetId: studioSnapshot.cardPresetId,
      message: studioSnapshot.cardMessage
    },
    promptSnapshot: {
      source: state.uploadedFile ? 'upload' : 'template'
    },
    aiParamsSnapshot: {
      requestedFrom: 'shopify-theme-design-studio'
    }
  });

  const listScenes = (client) =>
    client && client.cms && client.cms.listScenes
      ? client.cms.listScenes()
      : Promise.resolve([]);
  const listSceneEvents = (client, sceneId) =>
    client && client.cms && client.cms.listSceneEvents && sceneId
      ? client.cms.listSceneEvents(sceneId)
      : Promise.resolve([]);
  const listEventTemplates = (client, eventId) =>
    client && client.cms && client.cms.listEventTemplates && eventId
      ? client.cms.listEventTemplates(eventId)
      : Promise.resolve([]);
  const listTemplates = (client, query) =>
    client && client.cms && client.cms.listTemplates
      ? client.cms.listTemplates(query)
      : Promise.resolve([]);
  const listReferencePresets = (client, query) =>
    client && client.cms && client.cms.listReferencePresets
      ? client.cms.listReferencePresets(query)
      : Promise.resolve([]);

  const createCaseBody = async (payload, state) => {
    const formData = new FormData();
    formData.append('payload', JSON.stringify(payload));
    if (state.uploadedFile) formData.append('userImages', state.uploadedFile);
    if (state.surfaces && state.surfaces.front && state.surfaces.front.uploadedFile) {
      formData.append('frontUserImages', state.surfaces.front.uploadedFile);
    }
    if (state.surfaces && state.surfaces.back && state.surfaces.back.uploadedFile) {
      formData.append('backUserImages', state.surfaces.back.uploadedFile);
    }
    if (state.logoFile) formData.append('logoImages', state.logoFile);

    return formData;
  };

  const pollCase = async (client, caseId, onProgress) => {
    const deadline = Date.now() + 8 * 60 * 1000;
    while (Date.now() < deadline) {
      const caseData = await client.cases.get(caseId);
      if (onProgress) onProgress(caseData);
      if (TERMINAL_CASE_STATUSES.has(caseData.status)) return caseData;
      await sleep(2500);
    }
    throw new Error('AI generation timed out. The case remains available for retry.');
  };

  const requestPreview = async (options) => {
    const client = options.client;
    const state = options.state;
    const studioSnapshot = options.studioSnapshot || {};
    if (!client) throw new Error('POPTEX API client is unavailable');
    const configuredSurfaces = state.surfaces && Object.values(state.surfaces);
    const hasSurfaceSource = configuredSurfaces && configuredSurfaces.some((surface) => surface && (surface.templateId || surface.uploadedFile));
    if (!state.templateId && !state.uploadedFile && !hasSurfaceSource && !studioSnapshot.fixedArtworkUrl) {
      throw new Error('Choose a template or upload an image first');
    }
    const anonymousId = client.getAnonymousId();
    const payload = createDesignPayload(
      state,
      Object.assign({}, studioSnapshot, { anonymousId })
    );
    if (!payload.materialPresetId || !payload.packageConfig.presetId) {
      throw new Error('Material and packaging presets are required');
    }
    const selection = {
      sceneId: payload.sceneId,
      eventId: payload.eventId,
      templateId: payload.templateId
    };
    const uploadToken = await client.storefront.createCaseUploadToken(selection);
    const body = await createCaseBody(payload, state);
    const result = await client.cases.create(body, { uploadToken });
    const createdCase = result && result.case ? result.case : result;
    if (!createdCase || !createdCase.id) {
      throw new Error('Case creation response is missing a case ID');
    }

    state.caseId = createdCase.id;
    state.serverCase = createdCase;
    if (options.onProgress) options.onProgress(createdCase);
    const caseData = await pollCase(client, createdCase.id, options.onProgress);
    state.serverCase = caseData;
    const images = Array.isArray(caseData.displayImages)
      ? caseData.displayImages.filter((image) => image && image.url)
      : [];
    if (caseData.status === 'AI_FAILED' || images.length === 0) {
      throw new Error(
        (caseData.currentGeneration && caseData.currentGeneration.errorMessage) ||
          'AI generation failed'
      );
    }
    return {
      imageUrl: images[0].url,
      images,
      partial: caseData.status === 'AI_PARTIAL_FAILED' || images.length < 5,
      source: 'api',
      raw: caseData,
      payload
    };
  };

  window.PoptexDesignStudioApi = {
    listScenes,
    listSceneEvents,
    listEventTemplates,
    listTemplates,
    listReferencePresets,
    requestPreview
  };
})();
